/**
 * Báo cáo PDF kỳ công đã đóng — lưu hồ sơ HCNS (A4 dọc, font Noto Sans tiếng Việt).
 */
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const NOTO_TTF_URL =
  "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf";

const FONT_NAME = "NotoSans";

let fontBase64Promise = null;

function arrayBufferToBase64(buffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadNotoFontBase64() {
  if (!fontBase64Promise) {
    fontBase64Promise = fetch(NOTO_TTF_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Font HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then(arrayBufferToBase64);
  }
  return fontBase64Promise;
}

function hoursLabel(minutes) {
  const m = Number(minutes) || 0;
  if (m <= 0) return "0";
  const h = m / 60;
  return Number.isInteger(h) ? String(h) : h.toFixed(1).replace(/\.0$/, "");
}

function reviewLabel(status) {
  if (status === "approved") return "Đã duyệt";
  if (status === "rejected") return "Không duyệt";
  return "Chưa duyệt";
}

function explanationTypeLabel(type) {
  switch (type) {
    case "missing_checkout":
      return "Bổ sung giờ ra";
    case "missing_day":
      return "Bổ sung ngày công";
    default:
      return "Ghi chú";
  }
}

function explanationReviewLabel(status) {
  if (status === "approved") return "Đã duyệt";
  if (status === "rejected") return "Không duyệt";
  return "Chờ duyệt";
}

function formatWorkDate(ymd) {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd || "—";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function monthTitle(yearMonth) {
  const m = String(yearMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return yearMonth || "—";
  return `Tháng ${m[2]}/${m[1]}`;
}

/**
 * @param {object} data — response GET /closed-period-report
 * @param {{ filename?: string }} [opts]
 */
export async function downloadClosedPeriodReportPdf(data, opts = {}) {
  const period = data?.period || {};
  const employees = data?.employees || [];
  const explanations = data?.explanations || [];
  const branchLabel = data?.branch_label || "Tất cả chi nhánh";
  const yearMonth = period.year_month || "";

  let fontBase64 = null;
  try {
    fontBase64 = await loadNotoFontBase64();
  } catch (e) {
    console.warn("Không tải được font Noto, PDF có thể lỗi dấu tiếng Việt:", e);
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const font = fontBase64 ? FONT_NAME : "helvetica";
  if (fontBase64) {
    doc.addFileToVFS("NotoSans-Regular.ttf", fontBase64);
    doc.addFont("NotoSans-Regular.ttf", FONT_NAME, "normal");
    doc.setFont(FONT_NAME, "normal");
  }

  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 12;
  let y = 14;

  doc.setFontSize(14);
  doc.text("BÁO CÁO XÁC NHẬN / GIẢI TRÌNH GIỜ CÔNG", pageW / 2, y, { align: "center" });
  y += 7;
  doc.setFontSize(11);
  doc.text(monthTitle(yearMonth), pageW / 2, y, { align: "center" });
  y += 8;

  doc.setFontSize(9);
  const metaLines = [
    `Chi nhánh / phạm vi: ${branchLabel}`,
    `Trạng thái kỳ: Đã đóng`,
    `Ngày gửi yêu cầu xác nhận: ${period.confirmation_requested_at_fmt || "—"}`,
    `Hạn phản hồi nhân viên: ${period.employee_response_deadline_at_fmt || "—"}`,
    `Ngày đóng kỳ: ${period.closed_at_fmt || "—"}`,
    `Người đóng kỳ: ${period.closed_by_name || "—"}`,
    `Ngày xuất báo cáo: ${new Date().toLocaleString("vi-VN")}`,
  ];
  metaLines.forEach((line) => {
    doc.text(line, marginX, y);
    y += 5;
  });
  y += 2;

  doc.setFontSize(10);
  doc.text("1. Tổng hợp theo nhân viên", marginX, y);
  y += 2;

  const empBody = employees.map((emp, idx) => [
    String(idx + 1),
    emp.employee_name || "—",
    emp.employee_code || "—",
    emp.department_name || "—",
    String(emp.work_days ?? 0),
    hoursLabel(emp.work_minutes),
    hoursLabel(emp.ot_minutes),
    String(emp.leave_days ?? 0),
    emp.has_payroll_agreement ? "Có" : "Không",
    reviewLabel(emp.hr_review_status),
    String(emp.explanation_count ?? 0),
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [
      [
        "STT",
        "Họ tên",
        "MSNV",
        "Phòng ban",
        "Ngày công",
        "Giờ công",
        "Giờ OT",
        "Nghỉ phép",
        "Đồng ý",
        "HCNS",
        "GT",
      ],
    ],
    body: empBody.length
      ? empBody
      : [["—", "Không có nhân viên trong phạm vi", "", "", "", "", "", "", "", "", ""]],
    styles: {
      font,
      fontSize: 7,
      cellPadding: 1.2,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      font,
      fontStyle: "normal",
      fillColor: [185, 28, 28],
      textColor: 255,
      fontSize: 7,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 32 },
      2: { cellWidth: 16 },
      3: { cellWidth: 24 },
      4: { cellWidth: 12, halign: "center" },
      5: { cellWidth: 12, halign: "center" },
      6: { cellWidth: 12, halign: "center" },
      7: { cellWidth: 12, halign: "center" },
      8: { cellWidth: 12, halign: "center" },
      9: { cellWidth: 18, halign: "center" },
      10: { cellWidth: 8, halign: "center" },
    },
  });

  y = (doc.lastAutoTable?.finalY || y) + 8;
  if (y > 250) {
    doc.addPage();
    y = 16;
  }

  doc.setFont(font, "normal");
  doc.setFontSize(10);
  doc.text("2. Chi tiết giải trình theo ngày", marginX, y);
  y += 2;

  const exBody = explanations.map((ex, idx) => {
    const proposed =
      ex.proposed_check_in || ex.proposed_check_out
        ? `${ex.proposed_check_in || "—"} → ${ex.proposed_check_out || "—"}`
        : "—";
    return [
      String(idx + 1),
      ex.employee_name || "—",
      formatWorkDate(ex.work_date),
      explanationTypeLabel(ex.explanation_type),
      proposed,
      explanationReviewLabel(ex.review_status),
      ex.reason || "",
    ];
  });

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [["STT", "Nhân viên", "Ngày", "Loại", "Giờ đề xuất", "Duyệt", "Lý do"]],
    body: exBody.length
      ? exBody
      : [["—", "Không có giải trình trong kỳ", "", "", "", "", ""]],
    styles: {
      font,
      fontSize: 7,
      cellPadding: 1.2,
      overflow: "linebreak",
      valign: "top",
    },
    headStyles: {
      font,
      fontStyle: "normal",
      fillColor: [185, 28, 28],
      textColor: 255,
      fontSize: 7,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 28 },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 24 },
      4: { cellWidth: 22, halign: "center" },
      5: { cellWidth: 18, halign: "center" },
      6: { cellWidth: "auto" },
    },
  });

  y = (doc.lastAutoTable?.finalY || y) + 16;
  if (y > 240) {
    doc.addPage();
    y = 30;
  }

  doc.setFontSize(9);
  doc.text(
    "Báo cáo dùng để lưu hồ sơ sau khi chốt kỳ công. Số liệu giờ công lấy từ phiếu chấm đã checkout (gồm điều chỉnh đã duyệt).",
    marginX,
    y,
    { maxWidth: pageW - marginX * 2 }
  );
  y += 14;

  const colW = (pageW - marginX * 2) / 2;
  doc.text("Người lập (HCNS)", marginX + colW * 0.15, y);
  doc.text("Người kiểm tra / duyệt", marginX + colW + colW * 0.1, y);
  y += 22;
  doc.text("Ký, ghi rõ họ tên", marginX + colW * 0.15, y);
  doc.text("Ký, ghi rõ họ tên", marginX + colW + colW * 0.1, y);

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.text(
      `Trang ${i}/${pageCount} — Báo cáo giờ công ${monthTitle(yearMonth)}`,
      pageW / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: "center" }
    );
  }

  const filename =
    opts.filename || `bao-cao-ky-cong_${yearMonth || "thang"}.pdf`;
  doc.save(filename);
}
